import { useEffect, useRef, useState, useCallback } from "react";

// Logical canvas dimensions. CSS scales the element responsively.
const W = 720;
const H = 200;
const GROUND_Y = 168;
const BOT_X = 60;
const BOT_W = 36;
const BOT_H = 36;
const GRAVITY = 0.7;
const JUMP_VEL = 13;

const HS_KEY = "bot_runner_hs_v1";

function drawBot(ctx, x, y, frame) {
  // Body
  ctx.fillStyle = "#6366f1";
  ctx.fillRect(x, y + 8, BOT_W, BOT_H - 8);

  // Antenna (with little wobble)
  const wob = Math.sin(frame / 8) * 1;
  ctx.fillStyle = "#1e293b";
  ctx.fillRect(x + BOT_W / 2 - 1, y - 4, 2, 10);
  ctx.fillStyle = "#a855f7";
  ctx.beginPath();
  ctx.arc(x + BOT_W / 2 + wob, y - 5, 3.5, 0, Math.PI * 2);
  ctx.fill();

  // Visor (eyes)
  ctx.fillStyle = "#e0e7ff";
  ctx.fillRect(x + 6, y + 13, BOT_W - 12, 8);
  ctx.fillStyle = "#1e293b";
  ctx.fillRect(x + 10, y + 15, 4, 4);
  ctx.fillRect(x + BOT_W - 14, y + 15, 4, 4);

  // Smile
  ctx.fillStyle = "#1e293b";
  ctx.fillRect(x + 12, y + 27, 12, 2);

  // Feet (alternating animation)
  const footPhase = Math.floor(frame / 6) % 2;
  ctx.fillStyle = "#4338ca";
  if (footPhase === 0) {
    ctx.fillRect(x + 4, y + BOT_H, 10, 4);
    ctx.fillRect(x + BOT_W - 14, y + BOT_H - 2, 10, 4);
  } else {
    ctx.fillRect(x + 4, y + BOT_H - 2, 10, 4);
    ctx.fillRect(x + BOT_W - 14, y + BOT_H, 10, 4);
  }
}

function drawObstacle(ctx, o) {
  // Body
  ctx.fillStyle = "#a855f7";
  ctx.fillRect(o.x, GROUND_Y - o.h, o.w, o.h);
  // Top highlight
  ctx.fillStyle = "#c084fc";
  ctx.fillRect(o.x, GROUND_Y - o.h, o.w, 3);
  // Spikes
  ctx.fillStyle = "#7e22ce";
  const spikeCount = Math.max(2, Math.floor(o.w / 6));
  const spikeW = o.w / spikeCount;
  for (let i = 0; i < spikeCount; i++) {
    ctx.beginPath();
    ctx.moveTo(o.x + i * spikeW, GROUND_Y - o.h);
    ctx.lineTo(o.x + i * spikeW + spikeW / 2, GROUND_Y - o.h - 5);
    ctx.lineTo(o.x + i * spikeW + spikeW, GROUND_Y - o.h);
    ctx.closePath();
    ctx.fill();
  }
}

function drawCloud(ctx, c) {
  ctx.fillStyle = "rgba(196, 181, 253, 0.55)";
  ctx.beginPath();
  ctx.arc(c.x, c.y, 10, 0, Math.PI * 2);
  ctx.arc(c.x + 10, c.y - 4, 12, 0, Math.PI * 2);
  ctx.arc(c.x + 22, c.y, 9, 0, Math.PI * 2);
  ctx.fill();
}

export default function BotRunnerGame() {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const statusRef = useRef("idle"); // 'idle' | 'playing' | 'over'
  const highScoreRef = useRef(0);
  const finalScoreRef = useRef(0);

  const [status, setStatus] = useState("idle");
  const [, setTick] = useState(0); // forces overlay rerender on game over
  const [highScore, setHighScore] = useState(0);

  const setStatusBoth = useCallback((next) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  // Load high score once
  useEffect(() => {
    try {
      const hs = parseInt(localStorage.getItem(HS_KEY) || "0", 10);
      highScoreRef.current = isNaN(hs) ? 0 : hs;
      setHighScore(highScoreRef.current);
    } catch (e) {
      /* localStorage unavailable */
    }
  }, []);

  const resetState = useCallback(() => {
    stateRef.current = {
      botY: 0,
      botVel: 0,
      obstacles: [],
      clouds: [
        { x: 200, y: 50, speed: 0.5 },
        { x: 500, y: 30, speed: 0.3 },
        { x: 650, y: 60, speed: 0.4 },
      ],
      speed: 5.0,
      spawnTimer: 80,
      distance: 0,
      frame: 0,
    };
  }, []);

  const startGame = useCallback(() => {
    resetState();
    setStatusBoth("playing");
  }, [resetState, setStatusBoth]);

  const jump = useCallback(() => {
    const s = stateRef.current;
    if (!s) return;
    if (s.botY <= 0.5) {
      s.botVel = JUMP_VEL;
    }
  }, []);

  const handleAction = useCallback(() => {
    if (statusRef.current === "playing") jump();
    else startGame();
  }, [jump, startGame]);

  // Keyboard input
  useEffect(() => {
    const onKey = (e) => {
      if (
        e.code === "Space" ||
        e.key === " " ||
        e.key === "ArrowUp" ||
        e.key === "Enter"
      ) {
        e.preventDefault();
        handleAction();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleAction]);

  // Game loop — set up exactly once
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    // High-DPI setup
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    let raf;
    let lastTime = performance.now();

    function loop(now) {
      const dt = Math.min((now - lastTime) / 16.67, 2.5);
      lastTime = now;

      // Background
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#f8fafc");
      bg.addColorStop(1, "#eef2ff");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      const s = stateRef.current;

      // Clouds drift even on idle screen
      if (s) {
        for (const c of s.clouds) {
          c.x -= c.speed * dt;
          if (c.x < -40) c.x = W + 40;
          drawCloud(ctx, c);
        }
      }

      // Ground line
      ctx.fillStyle = "#cbd5e1";
      ctx.fillRect(0, GROUND_Y, W, 2);
      // Ground texture dashes
      ctx.fillStyle = "#94a3b8";
      const dashOffset = s ? Math.floor(s.distance) % 30 : 0;
      for (let x = -dashOffset; x < W; x += 30) {
        ctx.fillRect(x, GROUND_Y + 6, 12, 2);
      }

      if (s && statusRef.current === "playing") {
        s.frame += dt;

        // Physics
        s.botY += s.botVel * dt;
        s.botVel -= GRAVITY * dt;
        if (s.botY < 0) {
          s.botY = 0;
          s.botVel = 0;
        }

        // Move obstacles
        for (const o of s.obstacles) o.x -= s.speed * dt;
        s.obstacles = s.obstacles.filter((o) => o.x + o.w > -10);

        // Spawn obstacles
        s.spawnTimer -= dt;
        if (s.spawnTimer <= 0) {
          const h = 22 + Math.floor(Math.random() * 26);
          const w = 16 + Math.floor(Math.random() * 18);
          s.obstacles.push({ x: W + 30, w, h });
          // Spawn cadence loosens as speed climbs so it stays playable
          s.spawnTimer = 70 + Math.random() * 60 + 200 / s.speed;
        }

        // Difficulty ramp
        s.speed = Math.min(s.speed + 0.0028 * dt, 11);
        s.distance += s.speed * dt;

        // Collision detection (AABB)
        const bx = BOT_X + 4; // slight inset for forgiving hitbox
        const by = GROUND_Y - BOT_H - s.botY;
        const bw = BOT_W - 8;
        const bh = BOT_H;
        for (const o of s.obstacles) {
          const ox = o.x;
          const oy = GROUND_Y - o.h;
          if (
            bx < ox + o.w &&
            bx + bw > ox &&
            by < oy + o.h &&
            by + bh > oy
          ) {
            const finalScore = Math.floor(s.distance / 5);
            finalScoreRef.current = finalScore;
            if (finalScore > highScoreRef.current) {
              highScoreRef.current = finalScore;
              setHighScore(finalScore);
              try {
                localStorage.setItem(HS_KEY, String(finalScore));
              } catch (e) {
                /* ignore */
              }
            }
            setStatusBoth("over");
            setTick((t) => t + 1);
            break;
          }
        }
      }

      // Draw obstacles
      if (s) {
        for (const o of s.obstacles) drawObstacle(ctx, o);
      }

      // Draw bot
      const botY = s ? GROUND_Y - BOT_H - s.botY : GROUND_Y - BOT_H;
      drawBot(ctx, BOT_X, botY, s ? s.frame : 0);

      // HUD
      ctx.fillStyle = "#475569";
      ctx.font =
        "600 14px ui-sans-serif, system-ui, -apple-system, sans-serif";
      ctx.textAlign = "right";
      const live = s ? Math.floor(s.distance / 5) : 0;
      ctx.fillText(`Score ${String(live).padStart(5, "0")}`, W - 16, 24);
      ctx.fillStyle = "#94a3b8";
      ctx.font =
        "500 11px ui-sans-serif, system-ui, -apple-system, sans-serif";
      ctx.fillText(
        `High ${String(highScoreRef.current).padStart(5, "0")}`,
        W - 16,
        40,
      );
      ctx.textAlign = "left";

      raf = requestAnimationFrame(loop);
    }

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [setStatusBoth]);

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div
        className="relative rounded-2xl overflow-hidden shadow-lg border border-indigo-100 bg-white select-none cursor-pointer touch-none"
        onClick={handleAction}
        onTouchStart={(e) => {
          e.preventDefault();
          handleAction();
        }}
        role="button"
        tabIndex={0}
        aria-label="Bot Runner game"
      >
        <canvas
          ref={canvasRef}
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            aspectRatio: `${W} / ${H}`,
            imageRendering: "auto",
          }}
        />

        {/* Idle overlay */}
        {status === "idle" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm">
            <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-1">
              Bot Runner
            </h3>
            <p className="text-xs sm:text-sm text-slate-500 mb-4 text-center px-6">
              Help the bot dodge spikes while we finish maintenance.
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAction();
              }}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-transform hover:scale-[1.03] active:scale-[0.98]"
              style={{
                background:
                  "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              }}
            >
              Start
            </button>
            <p className="text-[11px] text-slate-400 mt-3">
              Press Space, tap, or click to jump
            </p>
          </div>
        )}

        {/* Game-over overlay */}
        {status === "over" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/85 backdrop-blur-sm">
            <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-1">
              Game over
            </h3>
            <p className="text-sm text-slate-600 mb-1">
              Score{" "}
              <span className="font-semibold text-indigo-600">
                {finalScoreRef.current}
              </span>
            </p>
            <p className="text-xs text-slate-500 mb-4">
              Best{" "}
              <span className="font-medium text-slate-700">{highScore}</span>
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAction();
              }}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-transform hover:scale-[1.03] active:scale-[0.98]"
              style={{
                background:
                  "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              }}
            >
              Play again
            </button>
            <p className="text-[11px] text-slate-400 mt-3">
              Or press Space to restart
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
